import { RefObject, useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Shrinks a single-line text node until it fits its own box.
 *
 * It MEASURES THE REAL ELEMENT rather than predicting its width. Two earlier
 * attempts predicted it and both clipped names on screen:
 *
 *   1. An average-glyph estimate (0.58em/char) — fine for lowercase latin,
 *      badly wrong for `Bot_09475C`, whose capitals and digits run nearer
 *      0.63em. It reported names as fitting when they did not.
 *   2. A canvas `measureText` of the same string — accurate about the glyphs,
 *      but it still had to be TOLD which font, weight and width budget the text
 *      would land in, and any drift in those (the card's border and padding,
 *      the seat's transform scale, a webfont that had not loaded yet) came
 *      straight back out as overflow.
 *
 * `scrollWidth` vs `clientWidth` has none of those inputs: the browser has
 * already laid the text out in the actual font, at the actual size, inside the
 * actual box. Width is linear in font size, so their ratio IS the correction.
 *
 * ## Why the work is batched
 *
 * Reading `clientWidth` forces a synchronous layout if the DOM has been written
 * since the last one. Fitting each seat independently means write, read, write,
 * read... and React runs every seat's layout effect back-to-back inside one
 * commit — so seat N's write invalidates the document and seat N+1's read pays
 * for a fresh full-document layout. That is O(seats) forced layouts on the table
 * open/swap path, which is exactly the path EN-10462 spent its budget on (8s
 * measured 10,709 forced UpdateStyleAndLayout in ~5s against a healthy ~750).
 *
 * So seats do not fit themselves. They enqueue, and one shared pass does all the
 * writes, then all the reads, then all the writes — ONE forced layout for the
 * whole table however many seats there are.
 *
 * The flush is a MICROTASK, not a rAF. React's commit runs every layout effect
 * synchronously inside one task, so a microtask queued from one of them runs
 * after the whole commit but still before the browser paints. A rAF would land a
 * frame later, flashing full-size names before they shrank.
 *
 * ## The readable floor
 *
 * Seats are painted through `scale(var(--seat-scale))`, so a font size in
 * LAYOUT px is not the size anyone reads. Landscape resolves that scale to
 * ~0.56 on a phone, which paints the seat name's 11.5px design size at 6.4px
 * (reported 2026-08-25 — the names were never shrunk, they were simply
 * scaled down with the seat).
 *
 * `readableMinPx` is the size the text STARTS at, in PAINTED px. The design size
 * is raised to meet it, exactly as `minScaledFontSize` used to do for the
 * balance row.
 *
 * `readableFloorPx` is how small the fit may then come back down, also in
 * painted px. It DEFAULTS to `readableMinPx` — hold the start size, and let a
 * name too wide for it truncate. That default is the older behaviour, kept
 * because it is the right one for a row that can afford to be shortened.
 *
 * The two were a single value for a while, which is the history worth knowing:
 *
 *   - As a floor on the STARTING size only (the fit free to win), it put
 *     `Apple_DDDDDD` at 8.91px next to `Mansanas` at 14.05px — visibly
 *     mismatched names across one table (reported 2026-08-27).
 *   - Made a hard floor to fix that, it stopped shrinking instead and truncated:
 *     "DiamondPlayer99…" (reported 2026-09-11).
 *
 * Splitting them is what lets a caller have both — start legible, shrink to fit,
 * and stop only at a floor low enough that real content never reaches it. The
 * seat name and the balance both take that shape; the size disparity is accepted
 * for them, because a truncated identity or a truncated money figure is worse
 * than a small one.
 *
 * The scale is MEASURED, not read from the variable: painted width over layout
 * width captures every transform in the chain, not just the one this module
 * happens to know about.
 *
 * ## Contract for callers
 *
 * The element must be `white-space: nowrap` and `overflow: hidden` — without
 * those the text wraps or spills and `scrollWidth` stops reporting the
 * single-line width. `text-overflow: ellipsis` is the backstop for content that
 * outruns `readableFloorPx`, so keep it on any caller that passes a NON-ZERO
 * floor (the seat balance does). A caller that passes 0 has no floor left to
 * outrun and may drop it — that is the seat NAME, deliberately (product decision
 * 2026-09-17); reinstating a floor there means reinstating the ellipsis in the
 * same change. This module also OWNS the element's `fontSize`, so the caller
 * must not set one in the style prop.
 */

/** Trims a hair off the fitted size: clientWidth/scrollWidth are integers, so a
 *  ratio derived from them can land a sub-pixel over the edge. */
const FIT_SAFETY = 0.99;

/**
 * Width, in LAYOUT px, treated as unusable on every measurement.
 *
 * `clientWidth` and `scrollWidth` are CSSOM integer properties — the browser
 * ROUNDS them. Text that truly needs 70.3px in a 70px box reports
 * `scrollWidth === clientWidth === 70`, so `needed <= available` reads as "it
 * fits" and nothing shrinks — while layout, which works in sub-pixels, overflows
 * and `text-overflow: ellipsis` cuts the tail.
 *
 * That is the whole of the 2026-09-15 report. Measured in Chromium with the real
 * Outfit face: of 12 truncations found across a 0.5-1.0 scale sweep, ALL 12 had
 * `scrollWidth === clientWidth` with a true overflow of 0.06-0.35px. `Connsack`
 * hit it at seat scale 0.628-0.630 and `The36Bus` at 0.633-0.635, while the
 * `Bot_*` names on the same table were far enough over the edge to be seen,
 * shrunk correctly, and rendered whole — which is why the bug looked like it
 * depended on the name rather than on the width, and why LONGER names were fine.
 *
 * 1px covers the worst case: up to half a pixel of rounding on each of the two
 * measurements. It costs ~1.4% of a 70px seat row, which is not visible; an
 * ellipsis is.
 */
const ROUNDING_GUARD_PX = 1;

/** Sub-pixel slack, so float noise is not read as overflow. */
const SUBPIXEL_EPSILON = 0.01;

/**
 * A refit landing this close to the size already on screen IS that size.
 *
 * Every fitted size is DERIVED from measurements — the painted scale sets the
 * start, the shrink scales from the start — and the painted width carries float
 * noise even against the fractional layout box: ~1e-5 relative in Chromium. On
 * the POV name plate that turned a 19.3207px / 19.3208px start into a
 * 15.98px / 15.9799px fit, alternately, every frame (screenshot #13): each
 * write resized the content-sized row this hook observes, which fired the
 * observer, which refit. 0.05px of font size is far below anything visible and
 * far above that noise.
 */
const SIZE_NOISE_PX = 0.05;

/**
 * Does the text REALLY overflow, when the integers say it exactly fills the box?
 *
 * `scrollWidth === clientWidth` is ambiguous and means two opposite things:
 *
 *   - the span is fit-content and the text fits it exactly — the ordinary case
 *     for any name short enough not to hit the card's max-width, and nothing
 *     should shrink;
 *   - the span is AT its max-width and the text overflows by less than the
 *     rounding — the blind spot, and it must shrink.
 *
 * Only a sub-pixel measurement separates them, so only the tie pays for one.
 * `getBoundingClientRect` and a Range over the contents are both fractional, and
 * both are painted px, so the seat's transform cancels in the comparison. A
 * Range is NOT clipped by `overflow: hidden`, which is what lets it report the
 * width the text wanted rather than the width it got.
 *
 * Returns false wherever the geometry is unavailable (jsdom, a hidden table) —
 * the same "cannot measure, do not guess" rule the rest of the module follows.
 */
const overflowsSubPixel = (el: HTMLElement): boolean => {
  if (typeof document === 'undefined' || typeof document.createRange !== 'function') return false;
  const boxWidth = el.getBoundingClientRect?.().width ?? 0;
  if (!boxWidth) return false;
  const range = document.createRange();
  range.selectNodeContents(el);
  const textWidth = range.getBoundingClientRect?.().width ?? 0;
  range.detach?.();
  if (!textWidth) return false;
  return textWidth > boxWidth + SUBPIXEL_EPSILON;
};

/** Refinement rounds after the first fit. Each is one more forced layout for the
 *  batch (not per element), so this is cheap — but it is not free either. */
const MAX_REFINEMENTS = 2;

type FitJob = {
  el: HTMLElement;
  designPx: number;
  minPx: number;
  /** Size to START at, in PAINTED px; 0 disables it. See "The readable floor". */
  readableMinPx: number;
  /**
   * How small the fit may shrink, in PAINTED px; 0 disables it.
   *
   * Defaults to `readableMinPx` — hold the start size and truncate. Pass
   * something lower to shrink past it instead, which is what a row whose
   * content must stay complete (the seat name, the balance) asks for.
   */
  readableFloorPx: number;
};

/**
 * The font size this module last WROTE for an element.
 *
 * It is what lets the reset write be skipped: an element already sitting at its
 * design size does not need touching, so a table of short names writes nothing
 * at all and the batch costs a single read.
 */
const appliedSize = new WeakMap<HTMLElement, number>();

const pending = new Map<HTMLElement, FitJob>();
let flushScheduled = false;

/**
 * The element's layout border-box width, in FRACTIONAL px.
 *
 * Not `clientWidth`: that is rounded to an integer, and the painted scale is a
 * ratio against the painted width, which is not. The rounding error then
 * depends on the text's own width — so on a CONTENT-sized row (the POV name
 * plate) every new font size measured a slightly different scale, which set a
 * different start size, which resized the row, which fired the ResizeObserver,
 * and the name ping-ponged between two sizes forever (screenshot #13). The
 * used width from the computed style is the unrounded box, so the ratio is the
 * transform scale alone and the start size cannot move under its own write.
 *
 * Falls back to `clientWidth` wherever the computed width is not a length (an
 * inline box, a non-rendering environment).
 */
const fractionalLayoutWidth = (el: HTMLElement): number => {
  const style = window.getComputedStyle(el);
  const width = parseFloat(style.width);
  if (!Number.isFinite(width) || width <= 0) return el.clientWidth;
  if (style.boxSizing === 'border-box') return width;
  const px = (value: string) => parseFloat(value) || 0;
  return (
    width +
    px(style.paddingLeft) +
    px(style.paddingRight) +
    px(style.borderLeftWidth) +
    px(style.borderRightWidth)
  );
};

/**
 * How much smaller than its layout box each element actually paints.
 *
 * A read-only pass, so it costs the batch one forced layout however many seats
 * are in it — the same O(1) the rest of this module is built around.
 */
const measurePaintedScale = (jobs: FitJob[]): Map<HTMLElement, number> => {
  const scales = new Map<HTMLElement, number>();
  for (const job of jobs) {
    const layoutWidth = fractionalLayoutWidth(job.el);
    const paintedWidth = job.el.getBoundingClientRect().width;
    // Not laid out (jsdom, hidden table): assume 1:1 rather than invent a floor.
    const usable = layoutWidth > 0 && paintedWidth > 0;
    scales.set(job.el, usable ? paintedWidth / layoutWidth : 1);
  }
  return scales;
};

/**
 * A job with its two bounds resolved into LAYOUT px.
 *
 * Both readable values are stated in painted px, so each has to be divided by
 * the element's painted scale before it can be compared with a font size. Doing
 * that once per flush keeps the scale out of the measure/shrink loop.
 */
type ResolvedJob = { job: FitJob; startPx: number; floorPx: number };

const resolveBounds = (jobs: FitJob[], scales: Map<HTMLElement, number>): ResolvedJob[] =>
  jobs.map((job) => {
    const paintedScale = scales.get(job.el) ?? 1;
    const toLayout = (paintedPx: number) => (paintedPx > 0 ? paintedPx / paintedScale : 0);
    return {
      job,
      // Where the text STARTS. Lifting this is what stops a scaled-down seat
      // painting its design size far smaller than intended.
      startPx: Math.max(job.designPx, toLayout(job.readableMinPx)),
      // How small the fit may actually go. Separate from the start on purpose:
      // a caller can ask to begin at a legible size and still shrink past it to
      // fit, rather than holding that size and truncating. `minPx` remains the
      // layout-px backstop underneath both.
      floorPx: Math.max(job.minPx, toLayout(job.readableFloorPx))
    };
  });

/** Put every queued element back to its starting size, so it is measured at the
 *  size it should paint at rather than one fitted to a previous name. */
const resetToDesign = (resolved: ResolvedJob[]): void => {
  for (const { job, startPx } of resolved) {
    if (appliedSize.get(job.el) === startPx) continue;
    job.el.style.fontSize = `${startPx}px`;
    appliedSize.set(job.el, startPx);
  }
};

/**
 * One measure-and-shrink round over the whole batch. Returns the elements that
 * were shrunk, which are the only ones a further round could still improve.
 *
 * All the reads happen together, before any of the writes — that ordering is the
 * whole point of this module.
 */
const refine = (resolved: ResolvedJob[]): ResolvedJob[] => {
  const readings = resolved.map((entry) => ({
    entry,
    available: entry.job.el.clientWidth,
    needed: entry.job.el.scrollWidth
  }));

  const shrunk: ResolvedJob[] = [];
  for (const { entry, available, needed } of readings) {
    const { job, floorPx } = entry;
    // A zero on either side means the element is not laid out (jsdom, or a
    // hidden table) — nothing to measure, so leave it at the design size.
    if (!available || !needed) continue;
    // Clearly inside the box — the common case, and it costs one comparison.
    if (needed < available) continue;
    // Integers say it exactly fills the box, which is ambiguous. This is the
    // reported ellipsis: resolve it sub-pixel rather than assuming it fits.
    if (needed === available && !overflowsSubPixel(job.el)) continue;

    const current = appliedSize.get(job.el) ?? job.designPx;

    // Aim a pixel inside the box, so a fit derived from rounded integers cannot
    // land back on the edge it was just moved off. See ROUNDING_GUARD_PX.
    const usable = Math.max(1, available - ROUNDING_GUARD_PX);
    const next = Math.max(floorPx, current * (usable / needed) * FIT_SAFETY);
    // No progress means `readableFloorPx` has been reached. The text STAYS at
    // that size and its tail is left to the element's ellipsis. For the seat
    // rows that floor is set low enough that real content shrinks to fit long
    // before getting here, so this is a backstop against the absurd rather than
    // the ordinary outcome.
    if (next >= current) continue;

    job.el.style.fontSize = `${next}px`;
    appliedSize.set(job.el, next);
    shrunk.push(entry);
  }
  return shrunk;
};

/**
 * Puts back the size that was on screen wherever the refit only moved it by
 * noise (see SIZE_NOISE_PX). The intermediate writes all happen inside this one
 * task, so the box ends where it started and a ResizeObserver, which compares
 * sizes at the next frame, has nothing to report — the loop has no next turn.
 */
const keepUnchangedWithinNoise = (jobs: FitJob[], before: Map<HTMLElement, number>): void => {
  for (const { el } of jobs) {
    const previous = before.get(el);
    const next = appliedSize.get(el);
    if (previous === undefined || next === undefined || next === previous) continue;
    if (Math.abs(next - previous) >= SIZE_NOISE_PX) continue;
    el.style.fontSize = `${previous}px`;
    appliedSize.set(el, previous);
  }
};

const flush = (): void => {
  flushScheduled = false;
  const jobs = Array.from(pending.values()).filter((job) => job.el.isConnected);
  pending.clear();
  if (!jobs.length) return;

  const before = new Map<HTMLElement, number>();
  jobs.forEach(({ el }) => {
    const applied = appliedSize.get(el);
    if (applied !== undefined) before.set(el, applied);
  });

  const resolved = resolveBounds(jobs, measurePaintedScale(jobs));
  resetToDesign(resolved);
  let round = resolved;
  for (let pass = 0; pass < MAX_REFINEMENTS && round.length; pass += 1) {
    round = refine(round);
  }
  keepUnchangedWithinNoise(jobs, before);
};

const scheduleFlush = (): void => {
  if (flushScheduled) return;
  flushScheduled = true;
  if (typeof queueMicrotask === 'function') queueMicrotask(flush);
  else Promise.resolve().then(flush);
};

/** Test seam: run the pending batch synchronously instead of awaiting a tick. */
export const __flushShrinkToFit = (): void => {
  flushScheduled = false;
  flush();
};

export const useShrinkToFit = <T extends HTMLElement>(
  text: string | undefined | null,
  designPx: number,
  minPx: number,
  readableMinPx = 0,
  readableFloorPx = readableMinPx
): RefObject<T> => {
  const ref = useRef<T>(null);

  const enqueue = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    pending.set(el, { el, designPx, minPx, readableMinPx, readableFloorPx });
    scheduleFlush();
  }, [designPx, minPx, readableMinPx, readableFloorPx]);

  useLayoutEffect(() => {
    enqueue();

    const el = ref.current;
    const parent = el?.parentElement;
    let observer: ResizeObserver | undefined;
    // The box can change width without this component re-rendering — the seat
    // ring re-lays out on rotate, and Table View Size resizes the card.
    //
    // INVARIANT this depends on: the observed parent is a FIXED-WIDTH box
    // (seatStyles getSeatCardStyle sets `width: SEAT_CARD_WIDTH`), and the seat
    // is scaled by a transform, which ResizeObserver does not observe. So these
    // fire on real layout changes only — a handful of times, not per frame.
    //
    // Make the seat card content-sized, or animate/transition its width, and
    // every seat's observer starts firing every frame, each one feeding this
    // batch. If you are changing the card's width, check that first.
    if (parent && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => enqueue());
      observer.observe(parent);
    }

    // Webfonts arrive after first paint, so the first fit measured the fallback
    // face. This is the pass that re-fits the name the moment Outfit lands.
    let cancelled = false;
    const fonts = (document as { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts?.ready?.then) {
      fonts.ready
        .then(() => {
          if (!cancelled) enqueue();
        })
        .catch(() => undefined);
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (el) pending.delete(el);
    };
    // `text` is not read in here, but a new name needs a fresh measurement.
  }, [enqueue, text]);

  return ref;
};
